import { EnvironmentState } from '@abb-procure/api';
import {
  DialogService,
  FileHashPipe,
  LoadingIndicator,
} from '@abb-procure/common';
import {
  ExchangeRatesState,
  UserApiService,
  UserState,
} from '@abb-procure/data';
import { SearchState } from '@abb-procure/search';
import { SignalrService } from '@abb-procure/sessions';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivationStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  BrowserUtils,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppHeader } from './components/app-header/app-header.component';
import { AppNavigation } from './components/app-navigation/app-navigation.component';
import { AppNotifications } from './components/app-notifications/app-notifications.component';

@Component({
  selector: 'procure',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    RouterOutlet,
    AppHeader,
    LoadingIndicator,
    AppNotifications,
    AppNavigation,
    FileHashPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {
  readonly mainRef = viewChild('main', { read: ElementRef });

  protected readonly isLoading = signal(false);
  protected readonly initializationInProgress = signal(true);
  protected readonly isIframe = signal(false);
  protected readonly loginDisplay = signal(false);

  protected readonly isEnvironmentBannerEnabled =
    environment.isEnvironmentBannerEnabled;

  private readonly signalrService = inject(SignalrService);
  private readonly router = inject(Router);
  private readonly authService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userState = inject(UserState);
  private readonly searchState = inject(SearchState);
  private readonly exchangeRatesState = inject(ExchangeRatesState);
  private readonly environmentState = inject(EnvironmentState);
  private readonly userApiService = inject(UserApiService);

  private readonly msalGuardConfig = inject(
    MSAL_GUARD_CONFIG,
  ) as unknown as MsalGuardConfiguration;

  constructor() {
    this.searchState.initialise();
    this.exchangeRatesState.fetchExchangeRates();

    this.environmentState.setIsProduction(environment.production);

    this.environmentState.setIsEnvironmentBannerEnabled(
      environment.isEnvironmentBannerEnabled,
    );

    effect(() => {
      if (!this.userState.isForbidden()) {
        return;
      }

      const message = this.userState.additionalInfoMessage() ?? '';

      this.dialogService.showOkDialog({
        title: 'Account Error',
        text: message,
        base: true,
        blackout: true,
        primaryAction: 'Logout',
        additionalInfo: this.userState.additionalInfo(),
        closeCallback: () => {
          this.authService.logoutRedirect({
            account: this.authService.instance.getActiveAccount(),
          });
        },
      });
    });

    this.destroyRef.onDestroy(() => {
      this.signalrService.disconnectHub();
    });
  }

  ngOnInit(): void {
    this.initializationInProgress.set(true);

    this.isIframe.set(BrowserUtils.isInIframe() || BrowserUtils.isInPopup());

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        const endLoading =
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError;

        if (event instanceof ActivationStart) {
          this.isLoading.set(true);
        } else if (endLoading) {
          this.isLoading.set(false);
          this.initAuthentication();
        }

        if (event instanceof NavigationEnd && environment.production) {
          this.userApiService.checkUserSession();
        }
      });
  }

  login(): void {
    this.authService.loginRedirect({
      ...this.msalGuardConfig.authRequest,
    } as RedirectRequest);
  }

  private setLoginDisplay(): void {
    this.loginDisplay.set(
      this.authService.instance.getAllAccounts().length === 0,
    );
  }

  /**
   * If no active account set but there are accounts signed in, sets first account to active account
   * To use active account set here, subscribe to inProgress$ first in your component
   */
  private checkAndSetActiveAccount(): void {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length === 0
    ) {
      return;
    }

    const accounts = this.authService.instance.getAllAccounts();
    this.authService.instance.setActiveAccount(accounts[0]);

    this.initializationInProgress.set(false);
    this.signalrService.connect();
  }

  private initAuthentication(): void {
    if (!this.initializationInProgress()) {
      return;
    }

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();

        if (!this.loginDisplay()) {
          return;
        }

        this.login();
      });
  }
}
